import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  History, ChevronDown, ChevronRight, RefreshCw, Plus, Pencil,
  Trash2, UserPlus, UserMinus, ArrowRight, Download,
} from "lucide-react";

// Iter-202b — Audit log viewer for custom-role mutations.
// Shows newest first. Actions: create | update | delete | assign | unassign.
// CEO/ADMIN (anyone with manage_custom_roles) can see this section.

const ACTION_META = {
  create:   { icon: Plus,        color: "emerald", label: "Création" },
  update:   { icon: Pencil,      color: "blue",    label: "Modification" },
  delete:   { icon: Trash2,      color: "rose",    label: "Suppression" },
  assign:   { icon: UserPlus,    color: "indigo",  label: "Assignation" },
  unassign: { icon: UserMinus,   color: "amber",   label: "Désassignation" },
};

const COLOR_CLASSES = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blue:    "bg-blue-50 text-blue-700 border-blue-200",
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
};

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 86400 * 30) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch (_e) { return iso; }
}

function formatAbsolute(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) { return iso; }
}

function ChangeSummary({ entry }) {
  const { action, diff, before, after, target_user_name, unassigned_from_n_users } = entry;
  if (action === "create") {
    return (
      <div className="text-xs text-zinc-600">
        Nouveau rôle <strong>{entry.role_name}</strong> avec {(after?.permissions || []).length} permission(s).
      </div>
    );
  }
  if (action === "delete") {
    return (
      <div className="text-xs text-zinc-600">
        Rôle <strong>{entry.role_name}</strong> supprimé{" "}
        {unassigned_from_n_users > 0 && (
          <span className="text-rose-600">
            (désassigné automatiquement de {unassigned_from_n_users} utilisateur{unassigned_from_n_users > 1 ? "s" : ""})
          </span>
        )}
      </div>
    );
  }
  if (action === "assign") {
    return (
      <div className="text-xs text-zinc-600">
        <strong>{entry.role_name}</strong> assigné à <strong>{target_user_name}</strong>
      </div>
    );
  }
  if (action === "unassign") {
    return (
      <div className="text-xs text-zinc-600">
        <strong>{entry.role_name}</strong> retiré de <strong>{target_user_name}</strong>
      </div>
    );
  }
  // update
  const parts = [];
  if (diff?.name_changed) parts.push(<span key="n"><em>nom</em>: "{before?.name}" → "{after?.name}"</span>);
  if (diff?.description_changed) parts.push(<span key="d"><em>description</em> modifiée</span>);
  if ((diff?.perms_added || []).length > 0) {
    parts.push(<span key="pa" className="text-emerald-700">+{diff.perms_added.length} perm</span>);
  }
  if ((diff?.perms_removed || []).length > 0) {
    parts.push(<span key="pr" className="text-rose-700">−{diff.perms_removed.length} perm</span>);
  }
  return (
    <div className="text-xs text-zinc-600 flex flex-wrap items-center gap-x-2 gap-y-1">
      {parts.length > 0 ? parts.reduce((acc, el, i) => acc.concat(i === 0 ? [el] : [<span key={`s${i}`} className="text-zinc-300">·</span>, el]), []) : <span className="italic">Aucun changement détecté</span>}
    </div>
  );
}

function ExpandedDetail({ entry }) {
  const { diff, before, after, action } = entry;
  return (
    <div className="px-4 py-3 bg-zinc-50/50 border-t border-zinc-200 text-xs space-y-2" data-testid={`audit-detail-${entry.audit_id}`}>
      {action === "update" && diff?.perms_added?.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase text-zinc-500 mb-1">Permissions ajoutées</div>
          <div className="flex flex-wrap gap-1">
            {diff.perms_added.map((p) => (
              <span key={p} className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-mono text-[10px]">+ {p}</span>
            ))}
          </div>
        </div>
      )}
      {action === "update" && diff?.perms_removed?.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase text-zinc-500 mb-1">Permissions retirées</div>
          <div className="flex flex-wrap gap-1">
            {diff.perms_removed.map((p) => (
              <span key={p} className="px-2 py-0.5 bg-rose-100 text-rose-800 rounded font-mono text-[10px]">− {p}</span>
            ))}
          </div>
        </div>
      )}
      {action === "update" && diff?.name_changed && (
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase text-zinc-500">Nom :</span>
          <span className="line-through text-zinc-400">{before?.name}</span>
          <ArrowRight size={10} className="text-zinc-400" />
          <span className="font-semibold">{after?.name}</span>
        </div>
      )}
      <div className="text-[10px] text-zinc-400 font-mono pt-1">audit_id: {entry.audit_id}</div>
    </div>
  );
}

export default function CustomRolesAuditLog({ canManage = false }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/custom-roles/audit-log", { params: { limit: 200 } });
      setEntries(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger le journal d'audit");
    } finally { setLoading(false); }
  };

  const exportCsv = async () => {
    try {
      const params = actionFilter === "all" ? { limit: 5000 } : { limit: 5000, action: actionFilter };
      const r = await api.get("/admin/custom-roles/audit-log/export.csv", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.download = `custom_roles_audit_${actionFilter}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'export CSV");
    }
  };

  useEffect(() => { if (open && entries.length === 0) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (actionFilter === "all") return entries;
    return entries.filter((e) => e.action === actionFilter);
  }, [entries, actionFilter]);

  if (!canManage) return null;

  return (
    <div className="mt-10 border-t border-zinc-200 pt-8" data-testid="custom-roles-audit-log">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-3 mb-2 group"
        data-testid="audit-log-toggle"
      >
        {open ? <ChevronDown size={18} className="text-zinc-500" /> : <ChevronRight size={18} className="text-zinc-500" />}
        <History size={16} className="text-zinc-700" />
        <h2 className="font-heading text-xl font-bold tracking-tight group-hover:text-zinc-700">Journal d'audit</h2>
        <span className="text-xs text-zinc-500 ml-1">Qui a créé / modifié / assigné quels rôles, et quand.</span>
      </button>
      {open && (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {["all", "create", "update", "delete", "assign", "unassign"].map((a) => (
              <button
                key={a}
                onClick={() => setActionFilter(a)}
                data-testid={`audit-filter-${a}`}
                className={`text-xs px-3 h-8 rounded-md border ${actionFilter === a ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:bg-zinc-50"}`}
              >
                {a === "all" ? "Tous" : (ACTION_META[a]?.label || a)}
              </button>
            ))}
            <button
              onClick={load}
              data-testid="audit-reload"
              className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 h-8 border border-zinc-200 rounded-md hover:bg-zinc-50"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Rafraîchir
            </button>
            <button
              onClick={exportCsv}
              data-testid="audit-export-csv"
              title="Exporter le journal filtré au format CSV (conformité / audit externe)"
              className="inline-flex items-center gap-1.5 text-xs px-3 h-8 border border-zinc-200 rounded-md hover:bg-zinc-50"
            >
              <Download size={12} /> Exporter CSV
            </button>
          </div>

          {loading && entries.length === 0 ? (
            <div className="text-sm text-zinc-500 text-center py-10">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-zinc-500 text-center py-10 border border-dashed border-zinc-200 rounded-lg">
              Aucune entrée d'audit pour ce filtre.
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white divide-y divide-zinc-100">
              {filtered.map((e) => {
                const meta = ACTION_META[e.action] || { icon: History, color: "zinc", label: e.action };
                const Icon = meta.icon;
                const isExpanded = !!expanded[e.audit_id];
                const expandable = e.action === "update" && (
                  (e.diff?.perms_added?.length || 0) + (e.diff?.perms_removed?.length || 0) > 0
                  || e.diff?.name_changed
                );
                return (
                  <div key={e.audit_id} data-testid={`audit-entry-${e.audit_id}`}>
                    <div
                      className={`flex items-start gap-3 px-4 py-3 hover:bg-zinc-50 ${expandable ? "cursor-pointer" : ""}`}
                      onClick={() => expandable && setExpanded((m) => ({ ...m, [e.audit_id]: !m[e.audit_id] }))}
                    >
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono uppercase border ${COLOR_CLASSES[meta.color] || ""}`}>
                        <Icon size={10} /> {meta.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-zinc-900 truncate">{e.actor_name}</span>
                          <span className="text-[10px] text-zinc-500 font-mono">{e.actor_email}</span>
                        </div>
                        <ChangeSummary entry={e} />
                      </div>
                      <div className="text-right text-[10px] text-zinc-500 shrink-0" title={formatAbsolute(e.timestamp)}>
                        <div>{formatRelative(e.timestamp)}</div>
                        <div className="font-mono mt-0.5">{formatAbsolute(e.timestamp)}</div>
                      </div>
                      {expandable && (isExpanded
                        ? <ChevronDown size={14} className="text-zinc-400 mt-1" />
                        : <ChevronRight size={14} className="text-zinc-400 mt-1" />
                      )}
                    </div>
                    {isExpanded && expandable && <ExpandedDetail entry={e} />}
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-3 text-[10px] text-zinc-400 font-mono">
            {filtered.length} entrée(s) affichée(s) — limite 200 (rafraîchir pour la suite).
          </div>
        </div>
      )}
    </div>
  );
}
